/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { convertToSlug } from "../../../../support/helpers";
import Select, { MultiValue } from "react-select";
import { failureToast, successToast } from "../../../../support/utils";
import { defaultFAQ, IFAQ, UpdateFAQPayload } from "../../models/FAQ";
import { updateFAQ, viewFAQ } from "../../redux/FAQCRUD";
import { IFAQCategory } from "../../models/FAQCategory";
import { getAllFAQCategory } from "../../redux/FAQCategoryCRUD";

type UpdateFAQProps = {
  cancelUrl?: string;
};

export const UpdateFAQ = ({
  cancelUrl = `/content-management/faqs`,
}: UpdateFAQProps) => {
  const { id } = useParams<{ id: string }>();

  // const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [data, setData] = useState<IFAQ>(defaultFAQ);

  const [categories, setCategories] = useState<Array<IFAQCategory>>([]);
  const [selectedCategories, setSelectedCategories] = useState<
    MultiValue<IFAQCategory>
  >([]);

  const updateData = (fieldsToUpdate: Partial<IFAQ>) => {
    const updatedData = { ...data, ...fieldsToUpdate };
    setData(updatedData);
  };

  const handleFetch = async (id: string) => {
    setLoading(true);

    try {
      const [data, categories] = await Promise.all([
        viewFAQ(id),
        getAllFAQCategory(),
      ]);

      setData(data);
      setCategories(categories);
      setSelectedCategories(data.categories!);

      setLoading(false);
    } catch (error) {
      failureToast(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      handleFetch(id);
    }
  }, [id]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);

    try {
      const payload: UpdateFAQPayload = {
        title: data.title,
        title_description: data.title_description,
        description: data.description,
        button_title: data.button_title,
        url: data.url,
        categories: selectedCategories.map((cat) => cat.id),
      };
      await updateFAQ(data.id, payload);
      successToast("Category has been updated.");
      setLoading(false);
    } catch (error) {
      failureToast(error);
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Update FAQ</h3>
      </div>
      {/* begin::Form */}
      <form onSubmit={handleSubmit} className="form d-flex flex-center">
        <div className="card-body mw-800px py-20">
          {/* begin::Form row */}
          <div className="row mb-8">
            <label className="col-lg-3 col-form-label">Title</label>
            <div className="col-lg-9">
              <div className="spinner spinner-sm spinner-primary spinner-right">
                <input
                  placeholder="Enter title"
                  className="form-control form-control-lg form-control-solid"
                  type="text"
                  value={data.title}
                  onChange={(e) =>
                    updateData({
                      title: e.target.value,
                    })
                  }
                  required
                />
              </div>
            </div>
          </div>
          {/* end::Form row */}

          {/* begin::Form row */}
          <div className="row mb-8">
            <label className="col-lg-3 col-form-label">Title Description</label>
            <div className="col-lg-9">
              <div className="spinner spinner-sm spinner-primary spinner-right">
                <textarea
                  rows={2}
                  placeholder="Enter title description"
                  className="form-control form-control-lg form-control-solid"
                  value={data.title_description}
                  onChange={(e) =>
                    updateData({
                      title_description: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>
          {/* end::Form row */}

          {/* begin::Form row */}
          <div className="row mb-8">
            <label className="col-lg-3 col-form-label">Description</label>
            <div className="col-lg-9">
              <div className="spinner spinner-sm spinner-primary spinner-right">
                <textarea
                  rows={2}
                  placeholder="Enter description"
                  className="form-control form-control-lg form-control-solid"
                  value={data.description}
                  onChange={(e) =>
                    updateData({
                      description: e.target.value,
                    })
                  }
                  required
                />
              </div>
            </div>
          </div>
          {/* end::Form row */}

          {/* begin::Form row */}
          <div className="row mb-8">
            <label className="col-lg-3 col-form-label">URL</label>
            <div className="col-lg-9">
              <div className="spinner spinner-sm spinner-primary spinner-right">
                <textarea
                  rows={2}
                  placeholder="Enter url"
                  className="form-control form-control-lg form-control-solid"
                  value={data.url}
                  onChange={(e) =>
                    updateData({
                      url: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>
          {/* end::Form row */}

          {/* begin::Form row */}
          {data.url && (
            <div className="row mb-8">
              <label className="col-lg-3 col-form-label">URL</label>
              <div className="col-lg-9">
                <div className="spinner spinner-sm spinner-primary spinner-right">
                  <textarea
                    rows={2}
                    placeholder="Enter button title"
                    className="form-control form-control-lg form-control-solid"
                    value={data.button_title}
                    onChange={(e) =>
                      updateData({
                        button_title: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          )}
          {/* end::Form row */}

          {/* begin::Form row */}
          <div className="row mb-8">
            <label className="col-lg-3 col-form-label">FAQs</label>
            <div className="col-lg-9">
              <Select
                placeholder={`Select categories...`}
                value={selectedCategories}
                onChange={setSelectedCategories}
                getOptionValue={(model: IFAQCategory) => model.id.toString()}
                getOptionLabel={(model: IFAQCategory) => model.title}
                options={categories}
                isSearchable
                isMulti
                isClearable
              />
            </div>
          </div>
          {/* end::Form row */}

          {/* begin::Form row */}
          <div className="row">
            <label className="col-lg-3 col-form-label"></label>
            <div className="col-lg-9">
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary fw-bolder px-6 py-3 me-3"
              >
                {!loading && <span className="indicator-label">Save</span>}
                {loading && (
                  <span
                    className="indicator-progress"
                    style={{ display: "block" }}
                  >
                    Please wait...{" "}
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </button>
              <Link
                className="btn btn-color-gray-600 btn-active-light-primary fw-bolder px-6 py-3"
                to={cancelUrl}
              >
                Cancel
              </Link>
            </div>
          </div>
          {/* end::Form row */}
        </div>
      </form>
      {/* end::Form */}
    </div>
  );
};
